// LogOfferModal — capture verbal/written offers. Auto-computes ratio
// against asking and a +/− delta as the user types the amount.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AIHint,
  Btn,
  Chip,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  TextArea,
  TextInput,
} from "./ModalShell";
import type { LogOfferCtx } from "./FlowsProvider";
import { addBid } from "@/lib/proagentApi";
import { invalidateCases } from "@/lib/agentCases";

const KINDS = ["Verbal", "Written", "OTP signed"];
const VIA = ["Phone", "WhatsApp", "Email", "In person"];
const CONDITIONS = [
  "Subject to financing",
  "Subject to inspection",
  "Subject to sale",
  "Vacant possession",
  "Furniture included",
  "Cash buyer",
];

type Form = {
  buyer?: string;
  kind?: string;
  via?: string;
  amount?: string;
  deposit?: string;
  completion?: string;
  conditions?: string[];
  notes?: string;
  expires?: string;
};

export function LogOfferModal({
  open,
  ctx,
  onClose,
}: {
  open: boolean;
  ctx: LogOfferCtx | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ kind: "Verbal", ...(ctx?.prefill ?? {}) });
      setError(null);
    }
  }, [open, ctx]);

  const caseObj = ctx?.caseObj;
  const asking =
    caseObj?.type === "property" ? caseObj.askingPrice : 1_450_000;
  const listingId = caseObj?.type === "property" ? caseObj.listingId : undefined;

  const numericOffer = useMemo(() => {
    const cleaned = String(form.amount ?? "").replace(/[^\d.]/g, "");
    return cleaned ? parseFloat(cleaned) : NaN;
  }, [form.amount]);
  const pct = Number.isFinite(numericOffer) && asking
    ? ((numericOffer / asking) * 100).toFixed(1)
    : null;
  const delta = Number.isFinite(numericOffer) && asking
    ? Math.round(numericOffer - asking)
    : null;

  return (
    <ModalShell open={open} onClose={onClose} width={640} label="Log offer">
      <ModalHeader
        eyebrow={t("agent.flows.logOffer.eyebrow", { defaultValue: "Log offer" })}
        title={t("agent.flows.logOffer.title", { defaultValue: "New offer" })}
        hint={
          caseObj?.shortName ? (
            <>
              {t("agent.flows.forCase", { defaultValue: "For case" })}{" "}
              <span style={{ color: "var(--ag-accent)", fontWeight: 600 }}>{caseObj.shortName}</span>
            </>
          ) : (
            t("agent.flows.logOffer.hint", {
              defaultValue: "Capture details so the seller report stays accurate.",
            })
          )
        }
        onClose={onClose}
      />

      <ModalBody>
        <Field label={t("agent.flows.logOffer.buyer", { defaultValue: "Buyer" })}>
          <TextInput
            value={form.buyer}
            onChange={(v) => setForm((f) => ({ ...f, buyer: v }))}
            placeholder="Buyer name (or pick from leads)"
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t("agent.flows.logOffer.kind", { defaultValue: "Offer type" })}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {KINDS.map((k) => (
                <Chip
                  key={k}
                  active={form.kind === k}
                  onClick={() => setForm((f) => ({ ...f, kind: k }))}
                >
                  {k}
                </Chip>
              ))}
            </div>
          </Field>
          <Field label={t("agent.flows.logOffer.via", { defaultValue: "Received via" })}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {VIA.map((v) => (
                <Chip
                  key={v}
                  active={form.via === v}
                  onClick={() => setForm((f) => ({ ...f, via: v }))}
                >
                  {v}
                </Chip>
              ))}
            </div>
          </Field>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", gap: 12, alignItems: "end" }}>
          <Field
            label={t("agent.flows.logOffer.amount", { defaultValue: "Offer amount" })}
            hint={
              pct ? (
                <>
                  ≈ <b>{pct}%</b> of asking · {(delta ?? 0) >= 0 ? "+" : "−"}$
                  {Math.abs(delta ?? 0).toLocaleString()}
                </>
              ) : (
                <>
                  {t("agent.flows.logOffer.asking", { defaultValue: "Asking" })} $
                  {asking.toLocaleString()}
                </>
              )
            }
          >
            <TextInput
              mono
              value={form.amount}
              onChange={(v) => setForm((f) => ({ ...f, amount: v }))}
              placeholder="$1,420,000"
            />
          </Field>
          <Field label={t("agent.flows.logOffer.deposit", { defaultValue: "Deposit %" })}>
            <TextInput
              mono
              value={form.deposit}
              onChange={(v) => setForm((f) => ({ ...f, deposit: v }))}
              placeholder="5%"
            />
          </Field>
          <Field label={t("agent.flows.logOffer.completion", { defaultValue: "Completion" })}>
            <TextInput
              mono
              value={form.completion}
              onChange={(v) => setForm((f) => ({ ...f, completion: v }))}
              placeholder="12 weeks"
            />
          </Field>
        </div>

        <Field label={t("agent.flows.logOffer.conditions", { defaultValue: "Conditions" })}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CONDITIONS.map((c) => {
              const active = (form.conditions ?? []).includes(c);
              return (
                <Chip
                  key={c}
                  active={active}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      conditions: active
                        ? (f.conditions ?? []).filter((x) => x !== c)
                        : [...(f.conditions ?? []), c],
                    }))
                  }
                >
                  {c}
                </Chip>
              );
            })}
          </div>
        </Field>

        <Field
          label={t("agent.flows.logOffer.notes", { defaultValue: "Notes" })}
          hint={t("agent.flows.logOffer.notesHint", {
            defaultValue: "Buyer reasoning, body language, leverage clues — anything the seller should know.",
          })}
        >
          <TextArea
            value={form.notes}
            onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
            rows={3}
            placeholder="e.g. Maya pushed back on price citing 3 comparable units below $1.43M."
          />
        </Field>

        <Field label={t("agent.flows.logOffer.expires", { defaultValue: "Validity / expires" })}>
          <TextInput
            value={form.expires}
            onChange={(v) => setForm((f) => ({ ...f, expires: v }))}
            placeholder="48 hours from now"
          />
        </Field>

        <AIHint>
          {t("agent.flows.logOffer.ai", {
            defaultValue:
              "On save we'll add this to the offers ledger, recompute the offer indication band, draft a seller-update message, and suggest a counter range.",
          })}
        </AIHint>
      </ModalBody>

      {error && (
        <div
          style={{
            margin: "0 16px 8px",
            padding: "8px 10px",
            background: "var(--ag-bad-bg, rgba(191,56,56,0.08))",
            border: "1px solid var(--ag-bad, #bf3838)",
            borderRadius: 6,
            color: "var(--ag-bad, #bf3838)",
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      )}

      <ModalFooter leftHint={t("agent.flows.escCancel", { defaultValue: "Esc to cancel" })}>
        <Btn ghost onClick={onClose} disabled={submitting}>
          {t("agent.flows.cancel", { defaultValue: "Cancel" })}
        </Btn>
        <Btn
          primary
          disabled={submitting}
          onClick={async () => {
            setError(null);
            if (!listingId) {
              setError(t("agent.flows.logOffer.needPropertyCase", {
                defaultValue: "Log an offer from a property case so it ties to the listing.",
              }) as string);
              return;
            }
            if (!Number.isFinite(numericOffer) || numericOffer <= 0) {
              setError(t("agent.flows.logOffer.needAmount", {
                defaultValue: "Offer amount must be a positive number.",
              }) as string);
              return;
            }
            // Map FE "kind" enum to backend Bid status. "OTP signed"
            // implies the seller hasn't responded yet — closest map
            // is "received" with the OTP context preserved in notes.
            // "Written" / "Verbal" both start as "received" too.
            // The agent can flip to countered/accepted via the bid
            // detail UI (existing).
            setSubmitting(true);
            try {
              const annotations: string[] = [];
              if (form.kind && form.kind !== "Verbal") annotations.push(`Type: ${form.kind}`);
              if (form.via) annotations.push(`Via: ${form.via}`);
              if (form.deposit) annotations.push(`Deposit: ${form.deposit}`);
              if (form.completion) annotations.push(`Completion: ${form.completion}`);
              if (form.expires) annotations.push(`Expires: ${form.expires}`);
              if (form.conditions && form.conditions.length) {
                annotations.push(`Conditions: ${form.conditions.join(", ")}`);
              }
              if (form.notes?.trim()) annotations.push(form.notes.trim());
              await addBid(listingId, {
                bidder_name: form.buyer?.trim() ?? "",
                amount: numericOffer,
                status: "received",
                notes: annotations.join("\n"),
              });
              invalidateCases();
              onClose();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting
            ? t("agent.flows.logOffer.submitting", { defaultValue: "Logging…" })
            : t("agent.flows.logOffer.submit", { defaultValue: "Log offer" })}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
}
